import type { SectionKey } from '../api/navigation.ts';
import type { Department, FollowUpAssignee, FollowUpMade, RecordOpen, SentTo, Task, TaskEvent, TaskObject } from './model.ts';
import type { ClosedByRecord } from '../api/tasks.ts';
import { formatDate } from '../dates.ts';
import { holdsKind } from '../activity.ts';
import { STEP_WORDS } from '../marshalling.ts';

/**
 * The task view (Arthur's layout, A-#152), pure so it is tested without a browser. The same view everywhere a task
 * opens: a header naming the department, then the client, the object, who assigned it and when it is due, then the
 * completer's fields. Nothing here decides who may do what: the task's `may` and the server's lists do.
 */

/**
 * A-#195 1: a property's own page, inside its client's record; `visit` scrolls to that site visit (#site-visit-<id>,
 * the list item's id). Every link that means a property comes here.
 */
export const propertyPath = (clientId: string, propertyId: string, visit?: string) =>
  `/clients/${clientId}/property/${propertyId}${visit ? `#site-visit-${visit}` : ''}`;

/** "Property Task", "Social Work Task" and so on: the view's header. */
export const departmentHeader = (d: Department) => `${d} Task`;

/** The client-record section each department's work belongs to (150's DEPARTMENT_SECTION, the same five). */
export const DEPARTMENT_SECTION: Record<Department, SectionKey> = {
  'Social Work': 'sw', Finance: 'financials', Legal: 'legal', Property: 'property', Compliance: 'reports',
};
const SLUG: Partial<Record<SectionKey, string>> = {
  sw: 'social-work', financials: 'finance', legal: 'legal', property: 'property', reports: 'reports', documents: 'documents',
};

/**
 * Where the client's name goes: the client page at the task department's section (Property for the Property team),
 * when the reader holds that section; the client's dashboard otherwise, never a section that would refuse them.
 */
export function clientLink(clientId: string, department: Department, sections: readonly SectionKey[]): string {
  const s = DEPARTMENT_SECTION[department];
  return sections.includes(s) ? `/clients/${clientId}/${SLUG[s]}` : `/clients/${clientId}`;
}

/** The object's field label: the object is one field, named by what it is (no "RE:"). */
export const OBJECT_WORDS: Record<TaskObject['kind'], string> = {
  property: 'Property', upload: 'Document', clio_copy: 'Document', contact: 'Contact',
};

/**
 * Where the object opens: a property's own page (its card on the client's Property section, by ?visit=<id>, the
 * opener 122 made); a document in the client's Documents section; a contact's card in place ('sheet'). null when the
 * reader holds no section that would show it, so it is shown as words alone, never a link that refuses.
 */
export function objectLink(clientId: string, o: TaskObject, sections: readonly SectionKey[]): string | 'sheet' | null {
  if (o.kind === 'contact') return 'sheet';
  if (o.kind === 'property') return sections.includes('property') ? propertyPath(clientId, o.id) : null;
  return sections.includes('documents') ? `/clients/${clientId}/documents` : null;
}

/**
 * Who assigned it: whoever made the latest assignment to its current holder (assigned or reassigned, newest first),
 * else whoever made the task, else "A deadline rule" for a rule's task, else "Not recorded" (never the source's own
 * word, such as "Manual").
 */
export function assignedBy(t: Pick<Task, 'created_by' | 'source'>, events: readonly Pick<TaskEvent, 'kind' | 'by'>[]): string {
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const e = events[i]!;
    if ((e.kind === 'assigned' || e.kind === 'reassigned') && e.by) return e.by.name;
  }
  if (t.created_by) return t.created_by.name;
  return t.source === 'Rule' ? 'A deadline rule' : 'Not recorded';
}

const WHY: Record<FollowUpAssignee['why'], string> = { assigned: 'on the case', colleague: 'colleague', director: 'director', you: 'you' };

/**
 * The follow-up chooser's options (151): exactly the server's list, one option per person and department, the
 * department the follow-up is made in. Its value carries both, so the department sent is the one the person was
 * listed under.
 */
export function followUpOptions(people: readonly FollowUpAssignee[]): { value: string; label: string }[] {
  return people.map((p) => ({ value: `${p.id}|${p.department}`, label: `${p.name} (${p.department}, ${WHY[p.why]})` }));
}

/** An option's value back to what the follow-up sends; '' is the reader, in the task's department. */
export function followUpTarget(value: string): { assigned_to_user_id?: string; department?: Department } {
  if (!value) return {};
  if (!value.includes('|')) return { assigned_to_user_id: value };
  const [id, department] = value.split('|') as [string, Department];
  return { assigned_to_user_id: id, department };
}

/**
 * A follow-up carries the task's object (151): the same property, document or contact, when the reader sees it (an
 * object the reader cannot see is never on their task, so none is carried). The server still checks it.
 */
export function withObject<F extends object>(f: F, object: TaskObject | null): F & { object?: { kind: TaskObject['kind']; id: string } } {
  return object ? { ...f, object: { kind: object.kind, id: object.id } } : f;
}

/** "Sent to <person>", or to the department's queue (A-#154): never a blank status. */
export function sentToSaid(s: SentTo): string {
  return s.name ? `Sent to ${s.name}.` : `Sent to the ${s.department} queue.`;
}

/**
 * What to say of a follow-up once made (150): a follow-up made for someone the reader cannot see comes back as its id
 * and who it went to; it was made, and saying anything else (an error, nothing) would be wrong (A-#154: who, by name).
 */
export function followUpSaid(f: FollowUpMade | null | undefined): string | null {
  if (!f) return null;
  return 'shown' in f && f.shown === false
    ? `The follow-up is made. ${sentToSaid(f.sent_to)} It is with them, and not visible to you.`
    : 'The follow-up is made.';
}

/**
 * A logged call's follow-up, on the entry (A-#154): its status where the reader sees the task; where they cannot (150:
 * a reader with no tasks module), that it was sent, never a blank. Who it went to is said when it is made, not here:
 * naming them later would read the task past its row security.
 */
export function callFollowUpLine(status: string | null): string {
  return status ?? 'sent; it is with them, not visible to you';
}

/** A-#194: the record's own words, as the task view says which record closed it. */
export const RECORD_WORDS: Record<ClosedByRecord['kind'], string> = {
  site_visit: 'the site visit', visit: 'the visit', filing: 'the filing',
  // 154 (A-#203): met by the order's date, recorded against the order
  commission: 'the commission', oath_designation: 'the oath and designation',
  // 162 (P-35): met by the order's bond
  bond: 'the bond',
  // 167 (P-24, Q-D): a blocker's task, completed by the record that cleared the blocker
  commission_blocker: 'the record that cleared its blocker',
  // 173 (P-46): a marshalling task, completed by the step that met its rule (the generic words, for the kind alone)
  marshalling: 'the marshalling step',
};

/** "Completed by the site visit on 27 Oct 2026" (the visit, the filing, the commission, the oath and designation; A-#205). */
export function closedByRecordSaid(r: ClosedByRecord): string {
  // 173 (P-46): the step in the Finance panel's own words (STEP_WORDS, never retyped) and its bank, as the panel says it
  if (r.kind === 'marshalling' && r.step && r.institution)
    return `Completed by the step "${STEP_WORDS[r.step]}" at ${r.institution} ${r.on ? `on ${formatDate(r.on)}` : 'on a date not recorded'}`;
  return `Completed by ${RECORD_WORDS[r.kind]} ${r.on ? `on ${formatDate(r.on)}` : 'on a date not recorded'}`;
}

/**
 * Where THAT record opens (A-#194), by the server's ids, never a guess: a site visit, its property's page at that
 * visit (A-#195 1); a filing, its row on Reports; a welfare visit, its own sheet in place ('visit'). Null where the reader holds
 * no section that shows it.
 */
export function closedRecordLink(clientId: string, r: ClosedByRecord, sections: readonly SectionKey[]):
  { to: string; state: RecordOpen } | 'visit' | null {
  // deploy 8 (P-13): the one kind -> section map, shared with the Timeline and the closed task's facts
  if (!holdsKind(r.kind, sections)) return null;
  if (r.kind === 'site_visit') return r.property_id
    ? { to: propertyPath(clientId, r.property_id, r.id), state: { opened_from: 'task' } } : null;
  if (r.kind === 'filing') return r.deadline_id
    ? { to: `/clients/${clientId}/reports`, state: { show_filing: r.deadline_id, opened_from: 'task' } } : null;
  // 154 (A-#203): the order's row in the Legal section's Commission panel
  if (r.kind === 'commission' || r.kind === 'oath_designation' || r.kind === 'bond')
    return { to: `/clients/${clientId}/legal`, state: { show_order: r.id, opened_from: 'task' } };
  // 167 (P-24): a blocker's task: the client's Legal section, where its commission line is
  if (r.kind === 'commission_blocker') return { to: `/clients/${clientId}/legal`, state: { opened_from: 'task' } };
  // 173 (P-46): a marshalling step: the client's Finance section, where its bank's marshalling panel is
  if (r.kind === 'marshalling') return { to: `/clients/${clientId}/finance`, state: { opened_from: 'task' } };
  // A-#199 3: a welfare visit's sheet, as the Timeline's entryLink
  return 'visit';
}
